import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { formatDateTime } from "src/utils/date/dateHelper";
import { McGeneralDiseaseItem, mcGeneralDiseaseService } from "src/api/medcards/mcGeneralDiseaseService";
import { useTranslate } from "src/locales";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import { axiosInstance } from "src/api/config/axiosInstance";

const dateIcon = "/assets/icons/clarity_date-line.svg";
const noteIcon = "/assets/icons/material-symbols_note-outline.svg";
const editIcon = "/assets/icons/mdi_pencil-outline.svg";
const trashIcon = "/assets/icons/mynaui_trash.svg";

interface DiseaseCardProps {
  name: string;
  operationDate: string;
  operator: string;
  note: string;
  firstDiagnosisDate: string;
  endDate: string;
  spirometry: string;
  id: number;
  originalItem: McGeneralDiseaseItem;
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const InfoRow: FunctionComponent<{ icon: string; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      alignSelf: "stretch",
      borderTop: "1px solid rgba(199, 199, 199, 0.3)",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "6px 16px",
    }}
  >
    <Box
      sx={{
        alignSelf: "stretch",
        height: "32px",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "8px 0px",
        boxSizing: "border-box",
        gap: "8px",
      }}
    >
      <Box component="img" src={icon} sx={{ height: "16px", width: "16px", position: "relative" }} alt="" />
      <Box sx={{ width: "110px", position: "relative", fontWeight: 600, display: "inline-block", flexShrink: 0 }}>
        {label}
      </Box>
      <Box sx={{ position: "relative", fontWeight: 300 }}>{value}</Box>
    </Box>
  </Box>
);

const DiseaseCard: FunctionComponent<DiseaseCardProps> = ({
  name,
  operationDate,
  operator,
  note,
  firstDiagnosisDate,
  endDate,
  spirometry,
  id,
  originalItem,
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await mcGeneralDiseaseService.deleteGeneralDisease(id);
      onDelete?.(id);
      onDeleteSuccess?.();
      return true;
    } catch (error) {
      console.error("Error deleting disease:", error);
      onDeleteError?.();
      return false;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "318px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: "12.01px",
          color: "#101010",
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "318px",
            boxShadow:
              "0px 4.0019307136535645px 7px -5px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(199, 199, 199, 0.25)",
            borderRadius: "8px",
            backgroundColor: "#fff",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              backgroundColor: "#f9f9f9",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "8px 16px",
              fontSize: "16.01px",
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                minHeight: "48px",
                height: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "286px",
                  overflow: "hidden",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0px",
                  boxSizing: "border-box",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    width: "100%",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4,
                      maxHeight: "2.8em",
                    }}
                  >
                    {name}
                  </Box>
                  <Box
                    component="img"
                    src={trashIcon}
                    alt={t("disease.delete")}
                    onClick={handleDeleteClick}
                    sx={{ width: "24px", position: "relative", maxHeight: "100%", cursor: "pointer" }}
                  />
                  <Box
                    component="img"
                    src={editIcon}
                    alt={t("disease.edit")}
                    onClick={() => onEdit?.(originalItem)}
                    sx={{ width: "24px", height: "24px", marginLeft: "8px", position: "relative", cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <InfoRow icon={dateIcon} label={t("disease.operation-date")} value={operationDate} />
        <InfoRow icon={dateIcon} label={t("disease.operation-user-short")} value={operator} />
        <InfoRow icon={noteIcon} label={t("disease.note-section")} value={note} />
        <InfoRow icon={dateIcon} label={t("disease.first-diagnosis-date")} value={firstDiagnosisDate} />
        <InfoRow icon={dateIcon} label={t("disease.end-date")} value={endDate} />
        <InfoRow icon={noteIcon} label={t("disease.spirometry")} value={spirometry} />
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
          }
        }}
        title={t("disease.delete-confirm-title")}
        content={t("disease.delete-confirm-content")}
        successMessage={t("disease.delete-success")}
        errorMessage={t("disease.delete-error")}
        hideSnackbar={true}
      />
    </>
  );
};

interface DiseasesCardListProps {
  data?: McGeneralDiseaseItem[];
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const DiseasesCardList: FunctionComponent<DiseasesCardListProps> = ({
  data = [],
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const [spirometryMap, setSpirometryMap] = useState<Record<number, string>>({});

  const STATIC_SPIROMETRY_NAMES: Record<number, string> = {
    1: "FEV1 ≥ %80",
    2: "FEV1 %60-80",
    3: "FEV1 %40-60",
    4: "FEV1 < %40",
  };

  useEffect(() => {
    const loadSpirometries = async () => {
      if (!data.length) return;

      const hasAnySpirometry = data.some(
        (item) => item.spirometryid && Number(item.spirometryid) > 0
      );

      if (!hasAnySpirometry) return;

      try {
        const ptId = data[0]?.ptid || 0;
        const response = await axiosInstance.post(
          "/medcard/v1/GeneralHealthInformation/GetSpirometryList/V1",
          { patientId: ptId }
        );

        const list: any[] = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const map: Record<number, string> = {};

        list.forEach((row: any) => {
          const id = Number(row?.ID ?? row?.id);
          const name = row?.NAME || row?.name;
          if (id && name) {
            map[id] = name;
          }
        });

        setSpirometryMap(map);
      } catch (error) {
        console.error("Error loading spirometry list for diseases card:", error);
      }
    };

    loadSpirometries();
  }, [data]);

  const items: DiseaseCardProps[] = useMemo(
    () =>
      data.map((item) => {
        const spirometryId = item.spirometryid ? Number(item.spirometryid) : 0;
        let spirometryName = "—";
        if (spirometryId) {
          const mappedName = STATIC_SPIROMETRY_NAMES[spirometryId] || spirometryMap[spirometryId];
          if (mappedName) {
            spirometryName = mappedName;
          } else {
            spirometryName = `Spirometri ID: ${spirometryId}`;
          }
        }

        return {
          name: item.name || "",
          operationDate: formatDateTime(item.ctime, "dd.MM.yyyy HH:mm") || "",
          operator: item.cuser || "",
          note: item.note || item.privatenotes || "",
          firstDiagnosisDate: formatDateTime(item.firstdiagdate, "dd.MM.yyyy HH:mm") || "",
          endDate: formatDateTime(item.enddate, "dd.MM.yyyy HH:mm") || "—",
          spirometry: spirometryName,
          id: item.id,
          originalItem: item,
          onDelete,
          onEdit: () => onEdit?.(item),
          onDeleteSuccess,
          onDeleteError,
        };
      }),
    [data, spirometryMap, onDelete, onEdit, onDeleteSuccess, onDeleteError]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "12px" }}>
      {items.map((item, index) => (
        <DiseaseCard key={`${item.name}-${item.operationDate}-${index}`} {...item} />
      ))}
    </Box>
  );
};

export default DiseasesCardList;


